/* eslint-disable @typescript-eslint/member-ordering */
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef, ViewChild, TemplateRef, ElementRef, isDevMode } from '@angular/core';
import { DashboardComponent, DashConf, DashModuleBase, DashModuleColumnConf, IDashConf, FontSizes } from 'starbase-dashboard';
import {
  StarbaseEntity, StarbaseDataServiceWithRequisites, StarbaseDataService,
  ContactModel, IMessage, StarbaseComponentMessage, UserAccountModel, UserGroupModel,
  FilterParams, IUserAccountModel, LU_ContactType, ICommType, removeDuplicateItems, toggleObjInArray
} from 'starbase-launcher';
import { ContactDashComponent, ContactDashConf, ContactPaneComponent, INavTab } from 'contact-dash';
import { ContactDataService } from '../services/contact-dash.service';
import { StarbaseLoginService } from 'starbase-login';
import { Observable, of, Subscription, forkJoin, Subject } from 'rxjs';
import {
  StarbaseNotificationObject, StarbaseNotificationService, StarbaseNotificationTypes, StarbaseNotificationSentiment
} from 'starbase-notification';
import { StarbaseComponentMessageService } from '../services/starbase-component-message.service';
import { debounceTime, map, share, takeUntil, finalize } from 'rxjs/operators';
import { StarbaseModalOverlayService } from 'modal-overlay';
import { StarbaseModalTypes, StarbaseModalDialogService, StarbaseModalOptions, StarbaseModalButton } from 'starbase-modal-dialog';
import { UserDataService } from '../services/user-data.service';
import { ContactPhoneService } from '../services/contact-phone.service';
import { ContactEmailService } from '../services/contact-email.service';
import { ContactPhone_ext } from '../models/contact-phone-ext';
import { ITableColumn, NewTableEditorComponent, NewTableEditorUserViewOptions } from 'new-table-editor';
import { ContactEmail_ext } from '../models/contact-email-ext';
import { UserGroupModel_ext } from '../models/lu_usergroup';
import { Log_ext } from '../models/log-ext';
import { LogDataService } from '../services/log-data.service';
import { PasswordScoreEnum } from 'account-management';
import { AppGlobalsService } from '../app-globals.service';
import { TransactionDashComponent, TransactionLogModel } from 'transaction-dash';
import { TransactionLogService } from '../services/transactionlog-data.service';
import { DatePipe } from '@angular/common';
import { LU_ContactType_ext } from '../models/lu_contacttype';
import { WebSocketService } from '../services/web-socket.service';
import { LuSubscriptionPriceService } from '../services/lu-subscriptionprice.service';
import { environment } from 'src/environments/environment';

export interface IDashTheme {
  name: string;
  class: string;
  color: string;
  secondaryColor?: string;
}

export class DashConfSave {
  moduleID: string;
  boxTitle: string;
  boxMainColor: string;
  boxMainIconClass: string;
  maximized: boolean;
  minimized: boolean;
  recordLimit: number;
  newDashConf: DashConf;
}

export class TimeZone {
  name: string;
  offset: string;
  dstOffset: string;
}

export const usTimezones = (): TimeZone[] =>
  [
    {
      name: 'America/Anchorage',
      offset: '-0900',
      dstOffset: '-0800'
    },
    {
      name: 'America/Chicago',
      offset: '-0600',
      dstOffset: '-0500'
    },
    {
      name: 'America/Denver',
      offset: '-0700',
      dstOffset: '-0600'
    },
    {
      name: 'America/Los_Angeles',
      offset: '-0800',
      dstOffset: '-0700'
    },
    {
      name: 'America/New_York',
      offset: '-0500',
      dstOffset: '-0400'
    },
    {
      name: 'America/Phoenix',
      offset: '-0700',
      dstOffset: '-0700'
    },
    {
      name: 'Pacific/Honolulu',
      offset: '-1000',
      dstOffset: '-1000'
    },
    {
      name: 'Coordinated Universal Time (UTC)',
      offset: 'UTC',
      dstOffset: 'UTC'
    }
  ];

export class DashPaneHandle<T extends StarbaseEntity> {
  private static _currentlyOpenPane = '';

  singlePaneData: T;
  dataService: StarbaseDataService<T> | StarbaseDataServiceWithRequisites<T>;
  readonly adminSession: boolean;
  accentColor: string;
  paneWidth?: string;
  fullScreenMode?: boolean;

  private readonly name: string;
  private paneOpen = false;

  constructor(name: string,
              dataSvc: StarbaseDataService<T> | StarbaseDataServiceWithRequisites<T>,
              admin = false,
              accentColor = '#333') {
    this.name = name;
    this.singlePaneData = undefined;
    this.dataService = dataSvc;
    this.adminSession = admin;
    this.accentColor = accentColor;
    this.paneWidth = '900px';
    this.fullScreenMode = false;
  }

  public static get currentlyOpenPane() {
 return this._currentlyOpenPane;
}

  public get handleName(): string {
 return this.name;
}

  public get handleType() {
 return typeof (this.singlePaneData);
}

  public get isPaneOpen(): boolean {
    return DashPaneHandle.currentlyOpenPane === this.name;
  }

  public openPane(paneObj?: T): void {
    this.singlePaneData = paneObj || this.singlePaneData;
    DashPaneHandle._currentlyOpenPane = this.name;
    this.paneOpen = true;
  }

  public closePane(): void {
    this.paneOpen = false;
    DashPaneHandle._currentlyOpenPane = '';
  }

  public toggleFullScreen(set?: boolean): void {
    this.fullScreenMode = set || !this.fullScreenMode;
  }
}

@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.css'],
  animations: [
    trigger(
      'dashPaneSlide',
      [
        state('true', style({ right: 0, top: 20, backgroundColor: 'red', position: 'absolute' })),
        transition(':enter', [
          style({
            backgroundColor: 'green',
            right: -100,
            top: 20,
            position: 'absolute'
          }),
          animate(500)
        ]),
        transition(':leave', [
          animate(500,
            style({
              right: -100,
              top: 20,
              backgroundColor: 'yellow',
              position: 'absolute'
            }))
        ])
      ])
  ]
})
export class AdminDashboardComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('mainDashboard', { static: true }) mainDashboardComponent: DashboardComponent;
  @ViewChild('dashOptionsModal', { static: true }) dashModalTemplate: TemplateRef<any>;
  @ViewChild('newContactModal', { static: true }) newContactModalTemplate: TemplateRef<any>;
  @ViewChild('editUserModal', { static: true }) editUserModalTemplate: TemplateRef<any>;
  @ViewChild('recordHistoryModal', { static: true }) recordHistoryModalTemplate: TemplateRef<any>;
  @ViewChild('expDateCol', { static: true }) expDateCol: TemplateRef<any>;
  @ViewChild('expDateColHeader', { static: true }) expDateColHeader: TemplateRef<any>;
  // @ViewChild('createSbUserStep2Override', { static: true }) createSbUserStep2Override: TemplateRef<any>;
  @ViewChild('contactPhonesTable') contactPhonesTable: NewTableEditorComponent<any>;
  @ViewChild('contactEmailsTable') contactEmailsTable: NewTableEditorComponent<any>;
  @ViewChild('sendEmailModal', { static: true }) sendEmailModal: TemplateRef<any>;
  @ViewChild('textForEmail') textForEmail: ElementRef;
  @ViewChild('subjectForEmail') subjectForEmail: ElementRef;
  @ViewChild('ctAssignModal', { static: true }) ctAssignModal: TemplateRef<any>;

  displayName: string;

  public dashModules: object[] = [];
  public dashBaseConfs: DashModuleBase<any>[];

  public newDashConfs: IDashConf[];
  public dashIcons: any[];
  public contactData = [];

  public contactDashModule = new ContactDashComponent(this.cd);
  public transLogDashModule = new TransactionDashComponent(this.cd);
  public contactPaneHandle = new DashPaneHandle<ContactModel>(
    'contactPane', this.contactSvc, this.isAdmin(), this.contactDashModule.boxMainColor);
  public transactionPaneHandle = new DashPaneHandle<TransactionLogModel>(
    'transactionPane', this.transLogSvc, this.isAdmin(), this.transLogDashModule.boxMainColor);
  contactPhoneCols: ITableColumn[] = ContactPhone_ext.ContactPhoneColumns();
  contactEmailCols: ITableColumn[] = ContactEmail_ext.ContactEmailColumns();
  contactPaneTableViewOpts = new NewTableEditorUserViewOptions(true, false, false);
  public editUserId: number;
  public promoteContact: ContactModel = null;
  userGroups: UserGroupModel[];
  public createWizOpen = false;
  public isDownloading = false;

  recordHistoryCols = Log_ext.LogColumns(false, false, false);
  recordHistoryData: FilterParams<Log_ext>;
  recordHistoryViewOpts = new NewTableEditorUserViewOptions(true);

  preLoadContactTypes: LU_ContactType[] = [];
  nonProtectedContactTypes: LU_ContactType_ext[] = [];
  contactList$: Observable<ContactModel[]>;

  userTimeZone$: Observable<TimeZone>;
  tooltipDelay = 500;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  UIDQueues: { module: string; UIDs: number[] }[] = [];

  booted = false;
  helpOpen = false;

  contactPaneTabs: INavTab[];
  showExpiryDateField = false;

  sendingEmailTo: ContactModel;
  sendingEmailAddress: string;

  darkMode = false;
  glassMode = false;

  toggleObjectInArray = toggleObjInArray;

  currentLoggedInUser: IUserAccountModel;
  currentLoggedInContact: ContactModel;

  contactsWatcherSubscription: Subscription;
  createContactWithUserSubscription: Subscription;
  contactExcelExportSubscription: Subscription;
  sbCmpNotifySubscription: Subscription;
  sbCmpCmdSubscription: Subscription;
  manageUserSubscription: Subscription;
  exportTransactionReportSubscription: Subscription;
  dashboardCustomizationsModalListener: Subscription;
  forkSubscriber: Subscription;

  subscriptionArray: Subscription[] = [];

  today = new Date();

  dashReSortOptions: any;
  theme: IDashTheme = { name: 'R.A.', class: 'ovcBg', color: '' };
  availableThemes: IDashTheme[];
  dashFontSize: 1 | 2 | 3 | 4 | 5; // standard "3 (medium)" size by default
  fontSizes = FontSizes;

  cancelData$ = new Subject<void>();
  newContactWizOpen: boolean;

  massCTs: LU_ContactType_ext[] = [];
  massContactsUIDs: number[] = [];
  massCTRemoveMode: boolean;

  activeUsers: number;

  private availableDashModuleBases: DashModuleBase<any>[] = []; // installed to this application

  constructor(private cd: ChangeDetectorRef,
              private notiSvc: StarbaseNotificationService,
              private transLogSvc: TransactionLogService,
              public sbCmpMsgSvc: StarbaseComponentMessageService,
              public appGlobalSvc: AppGlobalsService,
              protected contactSvc: ContactDataService,
              protected contactPhoneSvc: ContactPhoneService,
              protected contactEmailSvc: ContactEmailService,
              protected loginSvc: StarbaseLoginService,
              protected userSvc: UserDataService,
              protected subPlanSvc: LuSubscriptionPriceService,
              protected logSvc: LogDataService,
              protected overlaySvc: StarbaseModalOverlayService,
              protected modalSvc: StarbaseModalDialogService,
              public webSocketSvc: WebSocketService) {
    this.displayName = this.loginSvc.getDisplayName();
    const coRed = getComputedStyle(document.body).getPropertyValue('--RAred');
    this.theme.color = coRed;
    this.availableThemes = [
      { name: 'Plain', class: 'whiteBg', color: '#212529' },
      { name: 'Slate', class: 'grayBg', color: 'white' },
      { name: 'R.A.', class: 'ovcBg', color: coRed }
    ];
    this.userTimeZone$ = of(usTimezones().find(t => t.name === Intl.DateTimeFormat().resolvedOptions().timeZone) ||
      {
        name: 'Coordinated Universal Time (UTC)',
        offset: 'UTC',
        dstOffset: 'UTC'
      });
    this.dashReSortOptions = {
      onUpdate: (e: any) => {
        this.changeDashOrder(e.newIndex, e.oldIndex);
      }
    };
    this.contactPaneTableViewOpts.boldColloquialContent = false;
    this.contactPaneTableViewOpts.columnHeaderTextAlignment = 'left';
    // this.newDashConfs = [
    //   new ContactDashConf('Users & Contacts', this.contactDashModule.ModuleID, this.contactDashModule.boxMainIconClass),
    //   new DashConf(this.transLogDashModule.boxTitle, this.transLogDashModule.ModuleID, this.transLogDashModule.boxMainIconClass)
    // ];

  }

  ngOnInit() {
    this.dashboardCustomizationsModalListener = this.appGlobalSvc.callDashboardCustomizationsModal.subscribe(
      (d: boolean) => {
        if (d) {
          this.openDashOptions();
          this.appGlobalSvc.callDashboardCustomizationsModal.next(false);
        }
      }
    );
    this.subscriptionArray.push(this.dashboardCustomizationsModalListener);

    this.userTimeZone$ = this.appGlobalSvc.getStoredTimeZone().pipe(
      map(t => {
        if (!t) {
          return usTimezones().find(
            z => z.name === Intl.DateTimeFormat().resolvedOptions().timeZone)
            ||
            {
              name: 'Coordinated Universal Time (UTC)',
              offset: 'UTC',
              dstOffset: 'UTC'
            };
        } else {
          return t;
        }
      })
    );
    this.darkMode = this.appGlobalSvc.getStoredDarkModeSetting();
    this.glassMode = this.appGlobalSvc.getStoredGlassModeSetting();

    this.contactsWatcherSubscription = this.contactSvc.refreshTrigger$.subscribe(
      (d) => this.getDashboardRequisiteData(d)
    );
    this.subscriptionArray.push(this.contactsWatcherSubscription);

    this.createContactWithUserSubscription = this.contactSvc.triggerNewContactWithUser.subscribe(
      () => this.openCreateWizard()
    );
    this.subscriptionArray.push(this.createContactWithUserSubscription);

    this.contactExcelExportSubscription = this.contactSvc.exportDataEmitter.subscribe(
      (fp) => {
        this.exportContactExcel(fp);
      }
    );
    this.subscriptionArray.push(this.contactExcelExportSubscription);

    this.subscriptionArray.push(this.contactSvc.triggerMassContactTypeAssignment.subscribe((contacts: number[]) =>
      this.massContactTypes(contacts)));

    this.subscriptionArray.push(this.contactSvc.triggerMassContactTypeUnassignment.subscribe((contacts: number[]) =>
      this.massContactTypes(contacts, true)));


    this.appGlobalSvc.currentLoggedInUser.subscribe(a => {
      this.currentLoggedInUser = a;
      this.contactSvc.triggerRefresh(); // >> start the ball rolling.
    });
    this.appGlobalSvc.currentLoggedInContact.subscribe(a => this.currentLoggedInContact = a);

    this.sbCmpNotifySubscription = this.sbCmpMsgSvc.messageForUserEmitter.subscribe((m: IMessage) => this.handleDashComponentMsg(m));
    this.sbCmpCmdSubscription = this.sbCmpMsgSvc.messageCommandEmitter.subscribe((m: IMessage) => this.handleMessageCommands(m));
    this.subscriptionArray.push(this.sbCmpNotifySubscription);
    this.subscriptionArray.push(this.sbCmpCmdSubscription);

    this.exportTransactionReportSubscription = this.transLogSvc.exportDataEmitter.subscribe(
      (d: FilterParams<TransactionLogModel>) => {
        if (!this.isDownloading) {
          this.isDownloading = true;
          const excel$ = this.transLogSvc.getExcelReport(d)
            .pipe(share(),
                  finalize(() => this.isDownloading = false),
                  takeUntil(this.cancelData$));
          const notify = this.notiSvc.notificationBuilderAsync(
            'Please Wait',
            'Generating the report may take a moment.',
            null,
            excel$,
            1000,
            StarbaseNotificationSentiment.Notice,
          );
          this.notiSvc.postNotification(notify);
          excel$.subscribe(
            (content: any) => {
              const blob = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
              const downLink = document.createElement('a');
              downLink.href = window.URL.createObjectURL(blob);
              // eslint-disable-next-line max-len
              downLink.setAttribute('download', 'Transaction Data ' + new DatePipe('en-US').transform(new Date(), 'yyyy-MM-dd-HH-mm') + '.xlsx');
              downLink.click();
              // this.cd.detectChanges();
              notify.updateTitle('Success');
              notify.updateContent('Excel report generated.');
            },
            (e) => {
              notify.updateContent('Excel report failed to generate.');
              console.error(e);
              // this.cd.detectChanges();
            }
          );
        }
      }
    );
    this.subscriptionArray.push(this.exportTransactionReportSubscription);

    this.contactList$ = this.contactSvc.getAllData().pipe(
      map(c => c.map(d => d = new ContactModel().deserialize(d)))
    );

    this.contactSvc.triggerRefresh();

    this.defaultContactDash();
    this.defaultTxnLogDash();

    this.subPlanSvc.getAllData(this.subPlanSvc.getAllUri).subscribe(
      (plans) => {
        this.transLogSvc.PlanDataStore = plans.map(p => ({ id: p.uid, name: p.name }));
      }
    );

    setTimeout(() => {
      this.userSvc.getActiveUsers().subscribe((activeUsers) => {
        this.activeUsers = activeUsers;
      });
    }, 15000);
  }

  ngAfterViewInit() {
    const loadedTheme = this.appGlobalSvc.getDashTheme();
    this.tooltipDelay = this.appGlobalSvc.getStoredTooltipDelay() || 500;
    setTimeout(
      () => {
        this.dashFontSize = this.appGlobalSvc.getDashFontSize() || 2;
        if (loadedTheme) {
          this.switchTheme(loadedTheme);
        }
        this.dashModules = [
          ContactDashComponent,
          TransactionDashComponent
        ];
        this.availableDashModuleBases = [
          this.contactDashModule,
          this.transLogDashModule
        ];
      }, 1);

    setTimeout(() => this.getActiveUsers(), 3000);
  }

  ngOnDestroy() {
    this.cancelData$.next();
    this.cancelData$.complete();
    this.contactSvc.dataFilterParamsCancel$.next();
    this.contactSvc.dataGetAllCancel$.next();
    this.contactSvc.dataGetAllReqCancel$.next();
    this.subscriptionArray.forEach(
      s => {
        if (s) {
          s.unsubscribe();
        }
      }
    );
  }

  private getActiveUsers() {
    this.userSvc.getActiveUsers().subscribe((activeUsers) => {
      this.activeUsers = activeUsers;
    });
    setTimeout(() => this.getActiveUsers(), 60000);
  }

  /** Adjust multiple Contact Type assignment over multiple Contact records (add or remove links).  */
  private massContactTypes(contacts: number[], removeMode = false): void {
    this.massCTRemoveMode = removeMode;
    this.massCTs = []; // reset from last time
    this.massContactsUIDs = contacts;
    if (!contacts.length) {
    return;
  }
    let ctAssignModalObj: StarbaseModalOptions;
    const saveBtn = new StarbaseModalButton('Save', () => {
      this.massCTs = removeDuplicateItems(this.massCTs, 'uid');
      if (!removeMode) {
        this.contactSvc.saveMassCTAssignment({ contactUIDs: contacts, contactTypeUIDs: this.massCTs.map(c => c.uid) }).subscribe(
          () => {
            this.modalSvc.closeModal('User did the thing');
            this.contactSvc.triggerRefresh(undefined, true);
          },
          (e) => this.notiSvc.postNotification(
            this.notiSvc.notificationBuilder(
              'CT Assignment Failed', e['friendly'] + ' ' + e['serverMsg'][0], undefined, undefined, StarbaseNotificationSentiment.Error))
        );
      } else {
        this.contactSvc.saveMassCTUnassignment({ contactUIDs: contacts, contactTypeUIDs: this.massCTs.map(c => c.uid) }).subscribe(
          () => {
            this.modalSvc.closeModal('User did the thing');
            this.contactSvc.triggerRefresh(undefined, true);
          },
          (e) => this.notiSvc.postNotification(
            this.notiSvc.notificationBuilder(
              'CT Unassignment Failed', e['friendly'] + ' ' + e['serverMsg'][0], undefined, undefined, StarbaseNotificationSentiment.Error))
        );
      }
    }, () => this.massCTs.length > 0);
    // eslint-disable-next-line prefer-const
    ctAssignModalObj = new StarbaseModalOptions(
    removeMode ? 'Unassign Contact TYpes' : 'Assign Contact Types',
      undefined,
      [
        saveBtn,
        new StarbaseModalButton('Cancel', () => this.modalSvc.closeModal('User cancelled'))
      ],
      StarbaseModalTypes.Success,
      this.ctAssignModal);
    ctAssignModalObj.forcedHeight = '16em';
    ctAssignModalObj.forcedWidth = '40vw';
    this.modalSvc.postModal(ctAssignModalObj);
  }

  private getDashboardRequisiteData(onlyType?: object) {
    this.cancelData$.next();
    const contactDataForks: Observable<any>[] = [
      this.contactSvc.getAllRequisiteData<LU_ContactType_ext>(this.contactSvc.getUriByName('getAllContactTypesUri')),
      this.userSvc.getAllRequisiteData<UserGroupModel_ext>(this.userSvc.GetUserGroupsUri),
      this.contactSvc.getAllData()
    ];

    const allForks: Observable<any>[] = [];
    switch (true) {
      case onlyType instanceof ContactModel:
        // eslint-disable-next-line no-console
        console.info('only refreshing contact data');
        allForks.push(...contactDataForks);
        break;
      default:
        allForks.push(...contactDataForks);
        break;
    }

    this.forkSubscriber = forkJoin(allForks).pipe(takeUntil(this.cancelData$)).subscribe(
      ([ct, ug]: [LU_ContactType_ext[], UserGroupModel_ext[]]) => {
        this.preLoadContactTypes = ct;
        this.nonProtectedContactTypes = ct.filter(i => i.protected === false);
        this.userGroups = ug;
        this.contactSvc.ContactTypeDataStore = ct;
      },
      e => {
        console.error(e);
        this.notiSvc.postNotification(
          this.notiSvc.notificationBuilder('Error Loading',
            'Supporting data for dash modules failed to load.',
            undefined,
            undefined,
            StarbaseNotificationSentiment.Error
          )
        );
      },
      () => {
        this.contactDashModule.supportingDataArray = [
          this.preLoadContactTypes,
          [
            { group: 'Librarian', iconClass: 'fa-books', groupId: 1 },
            { group: 'Trial', iconClass: 'fa-watch', groupId: (isDevMode() || environment.testMode ? 3 : 2) }
          ]
        ];
        console.log(this.contactDashModule.supportingDataArray);
        if (!this.booted) {
          this.bootDashModules();
        }

        this.contactDashModule.setupSupportingData();
      }
    );
    this.subscriptionArray.push(this.forkSubscriber);
  }

  private defaultContactDash(): void {
    this.contactDashModule.dataService = this.contactSvc;
    this.contactDashModule.isAdmin = this.isAdmin();
    this.contactDashModule.maxRpp = 200;
    const cDashConf = new ContactDashConf(
      'Users & Contacts',
      ContactDashComponent.moduleID,
      this.contactDashModule.boxMainIconClass,
      this.contactDashModule.addNewIconClass,
      50);
    cDashConf.maxRpp = 200;
    cDashConf.backEndSearch = true;
    this.contactDashModule.backEndSearch = cDashConf.backEndSearch;
    this.contactDashModule.dashConf = cDashConf;
    this.contactDashModule.boxMainColor = this.contactDashModule.dashConf.boxMainColor;
    this.contactDashModule.supportingDataArray = [
      this.preLoadContactTypes,
      [
        { group: 'Librarian', iconClass: 'fa-books', groupId: 1 },
        { group: 'Trial', iconClass: 'fa-watch', groupId: (isDevMode() || environment.testMode ? 3 : 2) }
      ]
    ];
    const defaultCDHeaders = this.contactDashModule.defaultColumnHeaders;
    const rightHeader = new DashModuleColumnConf(
      'userAccount.subscriptionExpiryDate', 'Renew/Exp.', true, ['userAccount.subscriptionExpiryDate']);
    rightHeader.tooltip = 'Subscription renewal date or expiry date';
    // rightHeader.columnTemplate = this.expDateColHeader;
    this.contactDashModule.columnHeaders = [
      ...defaultCDHeaders,
      rightHeader
    ];
    this.contactDashModule.dashColumnContentN = this.expDateCol;
    console.log('Default Contact Dash init');
  }

  private defaultTxnLogDash(): void {
    this.transLogDashModule.dataService = this.transLogSvc;
    this.transLogDashModule.isAdmin = this.isAdmin();
    this.transLogDashModule.maxRpp = 200;
    const txnDashConf = new DashConf(
      this.transLogDashModule.boxTitle,
      this.transLogDashModule.ModuleID,
      this.transLogDashModule.boxMainIconClass,
      this.transLogDashModule.addNewIconClass,
      this.transLogDashModule.recordLimit,
      this.transLogDashModule.boxMainColor);
    txnDashConf.maxRpp = 200;
    this.transLogDashModule.dashConf = txnDashConf;
    this.transLogDashModule.columnHeaders = this.transLogDashModule.defaultColumnHeaders;
    console.log('Default Transaction Log Dash init');
  }

  private bootDashModules(): void {
    // eslint-disable-next-line no-console
    console.info('Booting Starbase Dash Modules...');

    // Prepare for default contingency
    const buildDefaultDashboard = () => {
      // eslint-disable-next-line no-console
      console.info('Resetting dash modules.');
      this.availableDashModuleBases.forEach(avail => this.dashModules.push(this.referenceModuleComponentObjectFromName(avail.ModuleID)));
      this.dashBaseConfs = this.availableDashModuleBases;
    };

    // First, load dash module order and prefs from AppGlobals.
    const storedDashModules = this.appGlobalSvc.getStoredDashOrder();
    this.dashModules = []; // classes
    this.dashBaseConfs = []; // configs
    this.dashIcons = []; // sidebar icons

    // Second, attempt to reconcile saved info with what the application supports.
    if (storedDashModules.modules && storedDashModules.modules.length === this.availableDashModuleBases.length) {
      try {
        for (let dm = 0; dm < storedDashModules.modules.length; dm++) {

          this.dashModules.push(this.referenceModuleComponentObjectFromName(storedDashModules.modules[dm]));

          if (!storedDashModules.confs[dm].newDashConf) {
            throw new Error('Dash Module had no associated IDashConf in memory: ' + storedDashModules.confs[dm].boxTitle);
          }
          this.dashBaseConfs.push(
            this.restoreFromDashConfSave(
              storedDashModules.confs[dm],
              this.findInstalledDash(storedDashModules.confs[dm].moduleID, this.availableDashModuleBases)
            ));
        }
      } catch (e) { // if attempt fails
        console.error(e);
        buildDefaultDashboard();
      }
    } else { // if reconciliation is not possible then build default
      buildDefaultDashboard();
    }

    // Third, set the sidebar's icons in the same order as dash was loaded.
    this.dashIcons = this.dashBaseConfs.map(d => d.boxMainIconClass);

    // Last, save the current configuration.
    this.saveDashModulesConfigs();
    this.booted = true;
  }

  private findDashConfInArray = (moduleId: string, arr: DashConfSave[]) =>
    arr.find(x => x.moduleID.includes(moduleId));
  private findInstalledDash = (moduleId: string, arr: DashModuleBase<any>[]) =>
    arr.find(x => x.ModuleID.includes(moduleId));
  private findNewDashConfInArray = (moduleId: string, arr: IDashConf[]) =>
    arr.find(x => x.moduleID.includes(moduleId));

  private referenceModuleComponentObjectFromName(name: string): object {
    /* eslint-disable curly */
    if (name === ContactDashComponent.moduleID)
        return ContactDashComponent;
      else if (name === TransactionDashComponent.moduleID)
        return TransactionDashComponent;
      else
        throw new Error('Dash Module Reference: name not recognized (' + name + ').');
  }

  private saveDashModulesConfigs(): void {
    const confsToSave = [];
    const moduleOrderToSave = [];
    for (let c = 0; c < this.dashBaseConfs.length; c++) {
      confsToSave.push(
        this.buildDashConfSaveFromDash(this.dashBaseConfs[c]));
      moduleOrderToSave.push(this.dashBaseConfs[c].ModuleID);
    }
    this.appGlobalSvc.setDashOrder({ modules: moduleOrderToSave, confs: confsToSave });
    console.log('saving dash confs');
    this.cd.markForCheck();
  }

  private restoreFromDashConfSave(saved: DashConfSave, moduleConf: DashModuleBase<any>): DashModuleBase<any> {
    if (saved.moduleID !== moduleConf.ModuleID) {
      throw new Error('Incompatible DashConfSave object passed.');
    }
    moduleConf.boxTitle = saved.newDashConf.boxTitle;
    moduleConf.boxMainColor = saved.newDashConf.boxMainColor || saved.boxMainColor;
    moduleConf.boxMainIconClass = saved.newDashConf.boxMainIconClass;
    moduleConf.startMaximized = saved.newDashConf.maximized;
    moduleConf.startMinimized = saved.newDashConf.minimized;
    moduleConf.recordLimit = saved.newDashConf.rpp;
    moduleConf.isAdmin = this.isAdmin();
    moduleConf.maxRpp = saved.newDashConf.maxRpp;
    moduleConf.dashConf = saved.newDashConf;
    return moduleConf;
  }

  private buildDashConfSaveFromDash(conf: DashModuleBase<any>): DashConfSave {
    const result = new DashConfSave();
    result.moduleID = conf.ModuleID;
    result.boxTitle = conf.boxTitle;
    result.boxMainColor = conf.boxMainColor;
    result.boxMainIconClass = conf.boxMainIconClass;
    result.maximized = conf.startMaximized;
    result.minimized = conf.startMinimized;
    result.recordLimit = conf.recordLimit;
    result.newDashConf = conf.dashConf;
    return result;
  }

  changeDashOrder(newIndex: number, oldIndex: number) {
    const movingModule = this.dashModules[oldIndex];
    const movingIcon = this.dashIcons[oldIndex];
    this.dashModules.splice(oldIndex, 1);
    this.dashModules.splice(newIndex, 0, movingModule);
    this.dashIcons.splice(oldIndex, 1);
    this.dashIcons.splice(newIndex, 0, movingIcon);

    this.saveDashModulesConfigs();
  }

  updateDashColor(cmd: { dash: string; data: any }) {
    if (!cmd.data) {
      return;
    }
    // if (cmd.dash === this.contactDashModule.boxTitle) {
    //   this.contactDashModule.boxMainColor = cmd.data;
    // } else if (cmd.dash === this.transLogDashModule.boxTitle) {
      this.transLogDashModule.boxMainColor = cmd.data;
    // }
    this.saveDashModulesConfigs();
  }

  updateDashConfs(dConf: DashConf) {
    this.saveDashModulesConfigs();
  }

  switchTheme(themeSelection: { name: string; class: string; color: string }) {
    this.theme = themeSelection;
    this.appGlobalSvc.setDashTheme(themeSelection);
    this.cd.detectChanges();
  }
  setDashFontSize(size: 1 | 2 | 3 | 4 | 5) {
    this.dashFontSize = size;
    this.dashBaseConfs.forEach(dash => {
      dash.fontSize = size;
      dash.dashConf.fontSize = size;
      this.contactDashModule.fontSize = size;
      this.contactDashModule.dashConf.fontSize = size;
    });
    this.appGlobalSvc.setDashFontSize(this.dashFontSize);
    this.saveDashModulesConfigs();
    this.cd.detectChanges();
  }

  /** Called by dash modules when opening a record. */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  updateUIDQueues(data: { module: string; UIDs: number[]}): void {
    const s = this.UIDQueues.find(m => m.module === data.module);
    if (s) {
      s.UIDs = data.UIDs;
    } else {
      this.UIDQueues.push(data);
    }
  }
  getUIDQueueForModule(module: string): number[] {
    try {
      return this.UIDQueues.find(q => q.module === module).UIDs || [];
    } catch {
      return [];
    }
  }

  public handleDashComponentMsg(msg: IMessage) {
    console.log('Dash Msg Proxy emitted: ' + msg.message);
    if (msg.quiet) {
      this.handleMessageCommands(msg);
      return;
    }
    const theNotification = this.notiSvc.notificationBuilder(
      'Message from Dashboard ', msg.message);
    if (msg.error) {
      theNotification.sentiment = StarbaseNotificationSentiment.Error;
      theNotification.duration = 7500;
    } else if (msg.success) {
      theNotification.sentiment = StarbaseNotificationSentiment.Success;
      theNotification.duration = 4000;
    } else if (msg.warning) {
      theNotification.sentiment = StarbaseNotificationSentiment.Warning;
      theNotification.duration = 7000;
    }

    if (msg.message.includes('birthday')) {
      if (this.appGlobalSvc.getStoredBirthdayNoticeSetting()) {
        theNotification.sentiment = StarbaseNotificationSentiment.Info;
        theNotification.duration = 7000;
      } else {
 return;
} // don't show bday notification if the option is turned off.
    }

    this.notiSvc.postNotification(theNotification);
  }

  private setCustomContactPaneTabs(): void {
    const cp = new ContactPaneComponent();
    cp.theRecord = this.contactPaneHandle.singlePaneData;
    let tabsToUse = cp.defaultTabs;
    tabsToUse = tabsToUse.filter(t => t.title !== 'Correspondences'); // This application does not (yet) have a Correspondence module.
    this.contactPaneTabs = [ ...tabsToUse ];
  }

  private handleMessageCommands(msg: IMessage): void {
    console.log('Dashboard component received a command msg');
    // ContactDashComponent // TransactionLogDashComponent
    if (msg.message.includes('minimized')) {
      const modDash = this.dashBaseConfs.find(m => m.ModuleID === msg.message.slice(0, -10));
      modDash.startMinimized = true;
      modDash.startMaximized = false;
    } else if (msg.message.includes('restored')) {
      const modDash = this.dashBaseConfs.find(m => m.ModuleID === msg.message.slice(0, -9));
      modDash.startMinimized = false;
      modDash.startMaximized = false;
    } else if (msg.message.includes('maximized')) {
      const modDash = this.dashBaseConfs.find(m => m.ModuleID === msg.message.slice(0, -10));
      modDash.startMaximized = true;
      modDash.startMinimized = false;
    }
    this.saveDashModulesConfigs();
  }

  public isAdmin(): boolean {
    return (this.loginSvc.isLoggedIn()) ? this.loginSvc.getUserModel().isAdmin() : false;
  }

  scrollToMenu(): void {
    // document.body.scrollTop = document.documentElement.scrollTop = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  focusOnDash(index: number) {
    document
      // .querySelector(`#${dashBoxTitle.replace(' ', '')}box`)
      .querySelectorAll('.dashFront')[index]
      .scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  handleDeleteUser(userUID: number): void {
    this.userSvc.deleteData(this.userSvc.deleteUri, userUID).subscribe(
      () => this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'User account deleted',
          'Who needs \'em?',
          undefined, undefined,
          StarbaseNotificationSentiment.Success
        )
      ),
      (e) => {
        console.error(e);
        if (e['serverMsg'] && e['serverMsg'][0] === 'User account has an active subscription and cannot be deleted.') {
          this.notiSvc.postNotification(
            this.notiSvc.notificationBuilder(
              'Error',
              'User account has an active subscription and cannot be deleted.',
              undefined, undefined,
              StarbaseNotificationSentiment.Error
            ));
        } else if (e['serverMsg'] && e['serverMsg'][0] === 'User account had a subscription and transactions.') {
          this.notiSvc.postNotification(
            this.notiSvc.notificationBuilder(
              'Error',
              'User account had transactions and cannot be deleted. Deactivate the user instead.',
              undefined, undefined,
              StarbaseNotificationSentiment.Error
            ));
        } else {
          this.notiSvc.postNotification(
            this.notiSvc.notificationBuilder(
              'Error',
              'Could not delete the user',
              undefined, undefined,
              StarbaseNotificationSentiment.Error
            ));
        }
      },
      () => this.contactSvc.triggerRefresh()
    );
  }

  closePanes(): void {
    this.overlaySvc.disableOverlay();
    this.contactPaneHandle.closePane();
    this.transactionPaneHandle.closePane();
  }

  openDashOptions() {
    const dashOptionsModal = new StarbaseModalOptions(
      'Customize your dashboard',
      '',
      [],
      StarbaseModalTypes.Notice,
      this.dashModalTemplate
    );
    dashOptionsModal.forcedWidth = '500px';
    this.modalSvc.postModal(dashOptionsModal);
  }

  openRecord(record: object) {
    console.log('Opening single record pane...' + record['uid']);
    if (record instanceof ContactModel) {
      this.contactPaneHandle.singlePaneData = record;
      this.contactPhoneSvc.currentContactId = record['contactUID'];
      this.contactPhoneSvc.currentContactObj = record as ContactModel;
      this.contactEmailSvc.currentContactId = record['contactUID'];
      this.contactEmailSvc.currentContactObj = record as ContactModel;
      this.overlaySvc.enableOverlay();
      this.setCustomContactPaneTabs();
      this.contactPaneHandle.openPane();
      if (this.contactPhonesTable) {
 this.contactPhonesTable.loadData();
}
      if (this.contactEmailsTable) {
 this.contactEmailsTable.loadData();
}
    } else if (record instanceof TransactionLogModel) {
        this.transactionPaneHandle.singlePaneData = record as TransactionLogModel;
        this.overlaySvc.enableOverlay();
        this.transactionPaneHandle.openPane();
    } else {
      console.error('Record type not recognized.');
    }
  }
  updateActiveContactUID(uid: number): void {
    this.contactSvc.getData(this.contactSvc.getOneUri, uid, 0).subscribe(
      cust => this.openRecord(new ContactModel().deserialize(cust)),
      () => {
        const c = new ContactModel();
        c.contactUID = uid;
        c.uid = uid;
        this.openRecord(c);
      }
    );
  }

  newRecordHandler(dashTitle: string) {
    console.log('New record requested for ' + dashTitle);
    this.resetCurrentContactInServices();
    if (dashTitle === this.contactDashModule.dashConf.boxTitle) {
      this.contactPaneHandle.singlePaneData = this.createNewContact();
      // this.setCustomContactPaneTabs();
      // this.newEntityModal(
      //   'Contact',
      //   this.newContactModalTemplate
      // ).subscribe(a => a ? this.contactPaneHandle.openPane() : this.contactPaneHandle.closePane());
      // this.contactPaneHandle.toggleFullScreen(true);
      this.newContactWizOpen = true;
      this.overlaySvc.enableOverlay();
    } else {
      console.error('New record type not recognized');
    }
  }

  private newEntityModal(
    entityName: string,
    modalTemplate: TemplateRef<any>,
    enableButtonCondition?: () => boolean): Observable<boolean> {
      const btnSubject = new Subject<boolean>();
      const goBtn = new StarbaseModalButton(
        'Go',
        () => {
          this.modalSvc.closeModal(`User proceeded with new ${entityName}.`);
          this.overlaySvc.enableOverlay();
          btnSubject.next(true);
        }
      );
      const cancelBtn = new StarbaseModalButton(
        'Cancel',
        () => {
        this.modalSvc.closeModal(`User cancelled new ${entityName}.`);
        btnSubject.next(false);
        }
      );
      this.modalSvc.postModal(
        new StarbaseModalOptions(
          `Create New ${entityName}`,
          '',
          [
            goBtn,
            cancelBtn
          ],
          StarbaseModalTypes.Success,
          modalTemplate
        )
      );
      return btnSubject;
    }

  /** Contacts Dash Support Functions */
  deactivateContactConfirm(event: { title: string; msg: string; obs: Subject<boolean> }) {
    this.modalSvc.postModal(
      new StarbaseModalOptions(
        event.title,
        event.msg,
        [
          new StarbaseModalButton(
            'Proceed',
            () => {
              event.obs.next(true);
              this.modalSvc.closeModal('User pressed proceed');
            }
          ),
          new StarbaseModalButton(
            'Cancel',
            () => {
              event.obs.next(false);
              this.modalSvc.closeModal('User canceled');
            }
          )
        ],
        StarbaseModalTypes.Warning
      )
    );
  }

  private createNewContact(): ContactModel {
    const c = {
      individual: true,
      contactUID: 0,
      uid: 0,
      lastName: '',
      organization: '',
      active: true,
      contactTypes: []
    };
    return new ContactModel().deserialize(c);
  }

  deleteContact(c: ContactModel) {
    const yesBtn = new StarbaseModalButton('Delete forever', () => {
      this.contactSvc.deleteData(this.contactSvc.deleteUri, c.uid.toString()). subscribe(
        () => {
          this.notiSvc.postNotification(
            this.notiSvc.notificationBuilder(
              'Contact Deleted',
              'Welp, that happened.',
              undefined, undefined,
              StarbaseNotificationSentiment.Notice));
          this.closePanes();
          this.contactDashModule.refreshData();
        },
        () => {
          this.notiSvc.postNotification(
            this.notiSvc.notificationBuilder(
              'Deletion Failed',
              `Sorry, the action did not complete. \n
                You cannot delete contacts that have any linked data in the system.`,
                undefined, undefined,
                StarbaseNotificationSentiment.Error
            )
          );
        },
        () => {
          this.modalSvc.closeModal('User clicked yes');
          this.contactSvc.triggerRefresh();
        }
      );
    });
    const noBtn = new StarbaseModalButton('Cancel', () => {
      this.modalSvc.closeModal('User canceled');
    });

    this.modalSvc.postModal(
      new StarbaseModalOptions('Delete Contact?',
        `Do you really want to permanently erase
          \n\n ${c.getDisplayName()}? \n\n
          This cannot be undone!`,
        [yesBtn, noBtn],
        StarbaseModalTypes.Warning)
    );
  }

  public handleShareContact(contact: ContactModel): void {
    const cmd = new StarbaseComponentMessage(
      `clipboard::Contact::${location.origin}/fullScreenContact/${contact.contactUID}`,
      true
    );
    this.sbCmpMsgSvc.handleStarbaseComponentMsg(cmd);
  }

  private resetCurrentContactInServices(): void {
    this.contactPhoneSvc.currentContactId = 0;
    this.contactEmailSvc.currentContactId = 0;
  }


  score = (pwd: string) => this.userSvc.getRequisiteData(this.userSvc.PasswordScoreUri + pwd).pipe(
    debounceTime(1000),
    map(s => s as PasswordScoreEnum)
  );

  checkEmailTaken = (email: string) => this.userSvc.getRequisiteData(this.userSvc.UsernameAllowedUri + email).pipe(
    debounceTime(1000)
  );


  handleRecordHistoryRequest(guid: string) {
    this.sbCmpMsgSvc.recordHistoryEmitter.emit(guid);
  }

  promoteContactToUser(contact: ContactModel) {
    this.contactPaneHandle.closePane();
    this.overlaySvc.disableOverlay();
    this.promoteContact = contact;
    // this.prepCreateSbWizard();
    this.createWizOpen = true;
    this.overlaySvc.enableOverlay();
  }

  openCreateWizard() {
    // this.prepCreateSbWizard();
    this.createWizOpen = true;
    this.overlaySvc.enableOverlay();
  }


  handleCreateWizardCompleted(user: UserAccountModel) {
    user.userGroups.forEach(
      (gl) => {
        gl.userGroup = null;
      }
    );
    if (user.contact.commTypesPhone[0].phoneNumber === '') {
      user.contact.commTypesPhone = [];
    }
    this.userSvc.postData(this.userSvc.postUri, user).subscribe(
      (u: UserAccountModel) => {
        const message = new StarbaseComponentMessage('Contact + user successfully created', false);
        message.success = true;
        this.sbCmpMsgSvc.handleStarbaseComponentMsg(message);
        this.createWizOpen = false;
        this.overlaySvc.disableOverlay();
      },
      () => {
        const message = new StarbaseComponentMessage('Failed to create new contact + user', false);
        message.error = true;
        this.sbCmpMsgSvc.handleStarbaseComponentMsg(message);
        this.createWizOpen = false;
        this.overlaySvc.disableOverlay();
      }
    );
    if (this.promoteContact) {
      this.promoteContact = null; // reset for next instance of wiz
      this.overlaySvc.enableOverlay();
      this.contactPaneHandle.openPane();
    }
  }

  handleCreateContactWizard(contact?: ContactModel, restart?: boolean) {
    if (contact) {
      contact.commTypesPhone.map(p => p.smsSuffix = 'Unknown');
      this.contactSvc.postData(this.contactSvc.postUri, contact).subscribe(
        (data) => {
          this.newContactWizOpen = false;
          this.cd.detectChanges();
          this.overlaySvc.disableOverlay();
          if (restart) {
            this.newRecordHandler(this.contactDashModule.boxTitle);
          } else {
            const contactModel = Object.assign(new ContactModel(), data);
            this.openRecord(contactModel);
          }
          this.notiSvc.postNotification(
            this.notiSvc.notificationBuilder(
              'Success',
              'Successfully created new contact record.',
              undefined, undefined,
              StarbaseNotificationSentiment.Success
            )
          );
        },
        (e) => {
          console.error(e);
          this.notiSvc.postNotification(
            this.notiSvc.notificationBuilder(
              'Error',
              'Failed to save new contact record.',
              undefined, undefined,
              StarbaseNotificationSentiment.Error
            )
          );
        }
      );
    } else {
      console.warn('Wizard completed but no contact data received.');
      this.newContactWizOpen = false;
      this.overlaySvc.disableOverlay();
    }
  }

  handleCreateWizardCancelled() {
    this.promoteContact = null;
    this.createWizOpen = false;
    this.newContactWizOpen = false;
    this.overlaySvc.disableOverlay();
  }
  /** End Contacts Dash Support Functions */

  receiveSendMessage(data: [ContactModel, ICommType][]) {
    let recipients = '';
    if (data.length === 1) {
      this.sendMessageToContact(data[0][0], data[0][1]);
    } else {
      for (const m of data) {
        recipients += m[1].getCommAddress() + ',';
      }
      this.sendingEmailAddress = recipients.slice(0, -1);
      const sendModal = new StarbaseModalOptions(
        `Send an email to ${data.length} contacts`,
        ``,
        [],
        StarbaseModalTypes.Info,
        this.sendEmailModal
      );
      const sendBtn = new StarbaseModalButton(' Send',
        () => {
          const emailBody = this.textForEmail.nativeElement.value;
          const subject = this.subjectForEmail.nativeElement.value;
          if (emailBody === '') {
            this.notiSvc.postNotification(
              this.notiSvc.notificationBuilder('Email Message Required',
                'Please enter a message in your email.', undefined, 4500,
                StarbaseNotificationSentiment.Warning));
          } else {
            sendModal.working = true;
            this.contactSvc.postRequisiteData(
              this.contactSvc.sendMassEmailUri,
              {recipientIDs: data.map((d => d[0].contactUID)), body: emailBody, subject }
            ).subscribe(
              () => {
                this.notiSvc.postNotification(
                  this.notiSvc.notificationBuilder(
                    'Email Sent!',
                    `Your messages were sent.`,
                    undefined, 4250,
                    StarbaseNotificationSentiment.Success));
                this.modalSvc.closeModal('User pressed Send. Process succeeded.');
              },
              e => {
                this.notiSvc.postNotification(
                  this.notiSvc.notificationBuilder(
                    'Email failed.', 'Your messages could not be sent.',
                    undefined, 4250,
                    StarbaseNotificationSentiment.Error));
                console.error(e);
                sendModal.working = false;
              }
            );
          }
        });
      const cancelBtn = new StarbaseModalButton('Cancel',
        () => {
          this.modalSvc.closeModal('User pressed cancel');
        });

      sendModal.buttons = [sendBtn, cancelBtn];
      this.modalSvc.postModal(sendModal);
    }
  }

  sendMessageToContact(contact: ContactModel, comm: ICommType) {
    this.sendingEmailTo = contact;
    this.sendingEmailAddress = comm.getCommAddress();
    const sendModal = new StarbaseModalOptions(
      `Send an email to ${contact.getDisplayName()}`,
      ``,
      [],
      StarbaseModalTypes.Info,
      this.sendEmailModal
    );
    const sendBtn = new StarbaseModalButton(' Send',
      () => {
        const emailBody = this.currentLoggedInUser
          ? this.currentLoggedInUser.userName + ' says: ' + this.textForEmail.nativeElement.value
          : `${this.loginSvc.getUserModel().displayName} says: ${this.textForEmail.nativeElement.value}`;
        const subject = this.subjectForEmail.nativeElement.value;
        if (emailBody === '') {
          this.notiSvc.postNotification(
            this.notiSvc.notificationBuilder('Email Message Required',
              'Please enter a message body before sending your email.', undefined, 4500,
              StarbaseNotificationSentiment.Warning));
        } else {
          sendModal.working = true;
          this.sendActualEmail(contact, subject, emailBody, sendModal);
        }
      });
    const cancelBtn = new StarbaseModalButton('Cancel',
      () => {
        this.modalSvc.closeModal('User pressed cancel');
      });

    sendModal.buttons = [sendBtn, cancelBtn];
    this.modalSvc.postModal(sendModal);
  }

  /** Receives a message command from the Dashboard component
   * and handles interpreting what type of message needs to be sent
   * then hands off control to the appropriate function.  */
   interceptMessageCommand(data: [ContactModel, ICommType][]) {
    if (!data.length || !data[0].length) {
      console.warn('No Comm Type data received. Canceling action.');
      return false;
    }
    if (data[0][0] instanceof ContactModel) {
      this.receiveSendMessage(data as [ContactModel, ICommType][]);
    } else {
      console.warn('Send Message command received from dash module, but could not be interpreted.');
    } // else if (data[0][0] instanceof CorrespondenceModel) { this.sendNudgeToContact(data[0][0], data[0][1]); }
  }

  // Actually does the task of sending a Starbase Msg.
  private sendActualEmail(contact: ContactModel, subject: string, emailBody: string, modalContext?: StarbaseModalOptions) {
    this.contactSvc.postRequisiteData(
      this.contactSvc.sendEmailToContactUri + contact.uid.toString()
      + '&subject=' + subject,
      `"${emailBody}"`
    ).subscribe(
      () => {
        this.notiSvc.postNotification(
          this.notiSvc.notificationBuilder(
            'Email Sent!',
            `Your message was sent to ${contact.getDisplayName()}`,
            undefined, 4250,
            StarbaseNotificationSentiment.Success));
        modalContext.closeModal('User pressed Send. Process succeeded.');
        if (this.modalSvc.isCurrentlyModal()) {
          this.modalSvc.closeModal('User pressed Send. Process succeeded.');
        }
      },
      e => {
        this.notiSvc.postNotification(
          this.notiSvc.notificationBuilder(
            'Email Unhappy.', 'Your message could not be sent.',
            undefined, 4250,
            StarbaseNotificationSentiment.Error));
        console.error(e);
        // eslint-disable-next-line curly
        if (modalContext) modalContext.working = false;
      }
    );
  }

  private exportContactExcel(filters: FilterParams<ContactModel>) {
    if (!this.isDownloading) {
      this.isDownloading = true;
      const jobExcel$ = this.contactSvc.getContactsExcel(filters).pipe(share());
      const notifier = new StarbaseNotificationObject(
        'Downloading...',
        null,
        StarbaseNotificationSentiment.Notice,
        null,
        jobExcel$);
      jobExcel$.subscribe(
        (content: any) => {
          const blob = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const downLink = document.createElement('a');
          downLink.href = window.URL.createObjectURL(blob);
          // eslint-disable-next-line max-len
          downLink.setAttribute('download', 'Users & Contact Data ' + new DatePipe('en-US').transform(new Date(), 'yyyy-MM-dd-HH-mm') + '.xlsx');
          downLink.click();
          this.isDownloading = false;
          this.cd.detectChanges();
          notifier.updateTitle('Success');
          notifier.updateContent('Excel report successfully generated');
        },
        (e) => {
          console.error(e);
          this.isDownloading = false;
          this.cd.detectChanges();
          notifier.updateTitle('Error');
          notifier.updateContent('Failed to download Excel data.');
        }
      );
      this.notiSvc.postNotification(notifier);
    }
  }

  contactUpdateConfirm(event: { title: string; msg: string; obs: Subject<boolean> }) {
    this.modalSvc.postModal(
      new StarbaseModalOptions(
        event.title,
        event.msg,
        [
          new StarbaseModalButton(
            'Continue',
            () => {
              event.obs.next(true);
              this.modalSvc.closeModal('User pressed continue');
            }
          )
        ],
        StarbaseModalTypes.Warning
      ));
  }

  contactUpdated() {
    this.notiSvc.postNotification(
      this.notiSvc.notificationBuilder(
        'Contact Updated',
        'This Contact record has been updated by another user.',
        undefined, undefined,
        StarbaseNotificationSentiment.Info
      )
    );
  }

  toggleHelp() {
    this.helpOpen = !this.helpOpen;
  }
}
